/**
 * Domain model and API contract shared by the dashboard, the API Worker and backend services.
 * Response shapes here are the source of truth: the data source must return exactly these types.
 */

import type { EnrichmentSettings } from "./analytics";

export type ISODate = string;

export interface Money {
  /** Major units (e.g. dollars). Storage uses minor units; the API always returns major units. */
  amount: number;
  currency: string;
}

// ─── Enumerations ────────────────────────────────────────────────────────────

export const SEVERITIES = ["critical", "high", "medium", "low"] as const;
export type Severity = (typeof SEVERITIES)[number];

export const TIERS = ["T0", "T1", "T2", "T3", "T4"] as const;
export type Tier = (typeof TIERS)[number];

export const DETECTION_TIERS = ["D", "C", "J", "S"] as const;
export type DetectionTier = (typeof DETECTION_TIERS)[number];

export const ISSUE_STATES = [
  "detected",
  "grace",
  "mitigated",
  "verifying",
  "resolved",
  "excluded",
  "regressed",
] as const;
export type IssueState = (typeof ISSUE_STATES)[number];

export const COVERAGE_STATES = [
  "covered",
  "grace",
  "mitigated",
  "verifying",
  "graduated",
  "excluded",
  "unobserved",
] as const;
export type CoverageState = (typeof COVERAGE_STATES)[number];

export type Archetype =
  | "customer_support"
  | "commerce"
  | "fintech_ops"
  | "sales"
  | "coding"
  | "internal_ops"
  | "research"
  | "other";

/** Who the agent acts for / talks to. */
export type Audience = "external_user" | "internal_user" | "machine";
export type AutonomyLevel = "L0" | "L1" | "L2" | "L3" | "L4";
export type Environment = "prod" | "staging";
export type PrivacyMode = "full" | "redacted" | "features";

export const PILLARS = ["reliability", "safety", "security", "controls", "remediation"] as const;
export type Pillar = (typeof PILLARS)[number];
export type PillarScores = Record<Pillar, number>;

/**
 * Detector packs are an open registry (the core knows none of them by name). A pack bundles
 * signal types, action-class templates, control templates and scenario templates, and is activated
 * automatically from an agent's discovered capabilities. "universal" is always on.
 */
export type PackId = string;

export interface PackInfo {
  id: PackId;
  name: string;
  description: string;
  /** Capability that activates the pack, in plain words (e.g. "agent moves money"). */
  activatesWhen: string;
  /** Signal types the pack's detectors emit today. */
  signalTypes: string[];
  /** Specified for the pack but not detected yet. */
  plannedSignalTypes: string[];
  core: boolean;
}

// ─── Impact ──────────────────────────────────────────────────────────────────

/**
 * What an action affects, in its own units. Money is one kind; a coding agent changes files, a data
 * agent touches records, an email agent sends messages, an infra agent changes resources. Policies
 * translate impact into monetary loss only when pricing and settling claims.
 */
export const IMPACT_KINDS = ["monetary", "records", "messages", "resources", "compute", "files", "custom"] as const;
export type ImpactKind = (typeof IMPACT_KINDS)[number];

export interface Impact {
  kind: ImpactKind;
  amount: number;
  /** ISO-4217 code for monetary impact; otherwise a unit such as "rows", "emails", "pods", "files". */
  unit: string;
}

export type SignalFamily =
  | "action_safety"
  | "security"
  | "data_protection"
  | "output_integrity"
  | "control_flow"
  | "tool_use"
  | "integrity"
  | "outcomes"
  | "economics"
  | "reliability";

// ─── Agents & profiling ──────────────────────────────────────────────────────

export interface AgentSummary {
  id: string;
  name: string;
  description: string;
  archetype: Archetype;
  audience: Audience;
  autonomy: AutonomyLevel;
  environment: Environment;
  model: string;
  ars: number;
  tier: Tier;
  arsDelta30d: number;
  arsTrend: number[];
  credibility: number;
  pillars: PillarScores;
  coverageState: CoverageState;
  openIssues: Record<Severity, number>;
  sessions24h: number;
  /** Share of control-expected actions that an executing platform reported a control outcome for. */
  controlAdoption: number;
  telemetryCompleteness: number;
  currentVersion: string;
  lastDeployAt: ISODate;
  activePacks: PackId[];
  /** Agent platform the customer built this agent on (null = built in-house on a framework). */
  platform: { name: string; slug: string } | null;
  framework: string | null;
  /** Total insured limit across the customer's active policies covering this agent. */
  insuredLimit: Money;
  policyIds: string[];
}

export interface ScoreFactor {
  label: string;
  /** Points; negative = currently dragging the score down, positive = upside if resolved. */
  impact: number;
  kind: "drag" | "upside" | "boost";
  issueId?: string;
}

export interface ScorePoint {
  date: ISODate;
  ars: number;
  pillars: PillarScores;
}

export interface AgentDetail extends AgentSummary {
  scoreHistory: ScorePoint[];
  topFactors: ScoreFactor[];
  dependencies: { kind: "model" | "mcp_server" | "api" | "framework"; name: string }[];
}

export interface AgentVersion {
  id: string;
  agentId: string;
  label: string;
  hash: string;
  model: string;
  deployedAt: ISODate;
  materialChange: boolean;
  ars: number | null;
  changes: { prompt: boolean; model: boolean; tools: boolean; policy: boolean };
  summary: string;
  sessions: number;
  /** What this version changed against the one before it, entry by entry (oldest version: what it started with). */
  changelog?: VersionChange[];
}

export interface VersionChange {
  kind: "model" | "prompt" | "tool_added" | "tool_removed" | "tool_changed" | "authority_added" | "authority_removed";
  /** One line, e.g. "Added tool stripe.refunds.create" or "Refunds over $1,000 need a human". */
  text: string;
  /** Previous → new value (model), or the tool's action class. */
  detail?: string;
  /** Widens what the agent can do: a new or redefined side-effecting tool, a widened prompt, a new model. */
  material?: boolean;
}

/** Universal action ontology (doc 11). `state` = an outward statement the agent makes. */
export type ActionVerb =
  | "read"
  | "create"
  | "update"
  | "delete"
  | "communicate"
  | "transact"
  | "execute"
  | "grant"
  | "state";

export type ActionObject =
  | "data_record"
  | "personal_data"
  | "financial_asset"
  | "file"
  | "message"
  | "identity"
  | "infrastructure"
  | "external_system"
  | "other";

export type ClassificationLayer = "mcp_annotation" | "catalog" | "schema" | "observed" | "llm";

export interface ToolClassification {
  tool: string;
  server?: string;
  verb: ActionVerb;
  object: ActionObject;
  actionClass: string;
  properties: Array<
    "side_effecting" | "reversible" | "external_facing" | "impact_bearing" | "idempotent" | "bulk"
  >;
  /** Impact kind this tool's actions are measured in, when it has one. */
  impactKind?: ImpactKind;
  risk: Severity | "none";
  confidence: number;
  layer: ClassificationLayer;
  why: string[];
  confirmed: boolean;
  /** Share of this tool's calls with a platform-reported control outcome (0–1). */
  controlCoverage: number;
  calls7d: number;
}

export interface AuthorityRule {
  id: string;
  area: string;
  rule: string;
  source: { kind: "system_prompt" | "policy_doc" | "rag_source"; ref: string; excerpt: string };
  /** An executing platform reported control outcomes for this rule's action class in the profiling window. */
  controlled: boolean;
}

export interface PackActivation {
  id: PackId;
  name: string;
  description: string;
  /** Core packs (universal) are always on; others activate from discovered capabilities. */
  core: boolean;
  enabled: boolean;
  reason: string;
  detectors: number;
}

export interface TopologyNode {
  id: string;
  label: string;
  kind: "agent" | "subagent" | "tool" | "mcp_server" | "model";
  risk?: Severity | "none";
}

export interface RiskProfile {
  agentId: string;
  status: "learning" | "ready_for_review" | "confirmed";
  archetype: Archetype;
  audience: Audience;
  autonomy: AutonomyLevel;
  packs: PackActivation[];
  tools: ToolClassification[];
  authority: AuthorityRule[];
  principals: {
    method: string;
    resolvedShare: number;
    b2bMapping: string;
  };
  baseline: { status: "learning" | "credible"; sessions: number; target: number; credibleSince?: ISODate };
  topology: { nodes: TopologyNode[]; edges: Array<[string, string]> };
  suggestions: SuggestedControl[];
  projection: {
    currentLimit: Money;
    projectedLimit: Money;
    currentTier: Tier;
    projectedTier: Tier;
    /**
     * What the agent has earned today (docs/05 §5: base × tier × seasoning × controls, capped): the
     * most new quotes can insure for it, all of its scenarios together. Grows as it seasons.
     */
    earnedLimit?: Money;
    /**
     * The formula's inputs other than the tier, so coverage can compute the earned limit at quote
     * time with the agent's current score tier.
     */
    limitInputs?: { baseLimit: number; cap: number; cleanMonths: number; controlAdoption: number; hitlOnCritical: boolean };
  };
}

/**
 * A control the platform that executes the agent's tools should enforce (docs/06). Platform-neutral:
 * `condition` uses the documented condition syntax over the action's fields, and the platform's
 * adapter translates it into its own policy language.
 */
export interface SuggestedControl {
  id: string;
  title: string;
  actionClass: string;
  /** When the control applies, e.g. `action.impact.amount > 200`. */
  condition: string;
  /** What the platform should do when the condition holds. */
  outcome: "deny" | "require_approval" | "semantic_check";
  /** Semantic checks to run (outcome `semantic_check`), e.g. `injection`, `intent_action_alignment`. */
  checks?: string[];
  reason: string;
  source: "authority_profile" | "issue" | "baseline";
  /** Tools of the agent in the action class. */
  tools: string[];
  issueId?: string;
  authorityRuleId?: string;
}

// ─── Traces & signals ────────────────────────────────────────────────────────

export type SpanKind = "agent" | "llm" | "tool" | "retrieval" | "control" | "handoff";
export type TraceOutcome = "success" | "failure" | "escalated" | "abandoned";
/**
 * Outcome of a control, as reported by the platform that executes the tool (`ti.control.outcome`):
 * allowed by policy, approved by a person, or blocked (attempted, not executed).
 */
export type ControlOutcome = "allowed" | "approved" | "blocked";

export interface SignalRef {
  id: string;
  type: string;
  severity: Severity;
}

export interface TraceSummary {
  traceId: string;
  sessionId: string;
  agentId: string;
  agentName: string;
  agentVersion: string;
  /** The customer's end user in this session (beneficiary under the customer's policies). */
  endUserId: string | null;
  endUser: string | null;
  startedAt: ISODate;
  durationMs: number;
  spanCount: number;
  tokens: number;
  costUsd: number;
  outcome: TraceOutcome;
  intent: string;
  signals: SignalRef[];
  /** Platform-reported control outcomes in the trace. */
  controls: ControlOutcome[];
  /** Total impact of the trace's side-effecting actions, one entry per kind/unit. */
  impact: Impact[];
}

export interface Span {
  spanId: string;
  parentSpanId: string | null;
  name: string;
  kind: SpanKind;
  startOffsetMs: number;
  durationMs: number;
  status: "ok" | "error";
  model?: string;
  tool?: string;
  actionClass?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  attributes: Record<string, string | number | boolean>;
  input?: string;
  output?: string;
  /** Control facts on a tool span, when an executing platform reported them. */
  control?: { outcome: ControlOutcome; rule?: string; policy?: string; approvedBy?: string; reportedBy: string };
  signalIds: string[];
}

export interface ChatMessage {
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  spanId?: string;
  name?: string;
}

export interface Signal {
  id: string;
  type: string;
  family: SignalFamily;
  severity: Severity;
  confidence: number;
  detector: string;
  detectorVersion: string;
  tier: DetectionTier;
  agentId: string;
  traceId: string;
  spanId?: string;
  ts: ISODate;
  explanation: string;
  issueId?: string;
  /** Pack that defines this signal type (null = core). */
  pack: PackId | null;
}

export interface TraceDetail extends TraceSummary {
  spans: Span[];
  messages: ChatMessage[];
  signalDetails: Signal[];
}

export interface SignalStat {
  type: string;
  family: SignalFamily;
  severity: Severity;
  tier: DetectionTier;
  count7d: number;
  deltaPct: number;
  trend: number[];
  agents: string[];
  precision: number;
  pack: PackId | null;
}

export interface SignalHeatCell {
  agentId: string;
  family: SignalFamily;
  count: number;
}

// ─── Issues ──────────────────────────────────────────────────────────────────

export type FixKind = "control" | "prompt_patch" | "tool_fix" | "probes";

export interface SuggestedFix {
  id: string;
  kind: FixKind;
  title: string;
  description: string;
  /** Unified diff (prompt patch), the control's condition, or a probe list. */
  body: string;
  backtest?: { window: string; wouldCatch: number; of: number; falsePositiveRate: number };
  status: "proposed" | "shadow" | "applied";
}

export interface IssueEvent {
  type:
    | "detected"
    | "notified"
    | "grace_started"
    | "grace_warning"
    | "mitigated"
    | "fix_declared"
    | "verification_passed"
    | "verification_failed"
    | "resolved"
    | "excluded"
    | "regressed"
    | "comment"
    | "extension_granted";
  at: ISODate;
  actor: string;
  note?: string;
}

export interface IssueSummary {
  id: string;
  title: string;
  agentId: string;
  agentName: string;
  severity: Severity;
  state: IssueState;
  pack: PackId;
  scenarioKey: string;
  signalTypes: string[];
  affectedSessions: number;
  affectedEndUsers: number;
  exposure: Money;
  firstSeen: ISODate;
  lastSeen: ISODate;
  graceDeadline: ISODate | null;
  graceStartedAt: ISODate | null;
  coverageState: CoverageState;
}

export interface IssueDetail extends IssueSummary {
  summary: string;
  rootCause: string;
  representativeTraceIds: string[];
  fixes: SuggestedFix[];
  timeline: IssueEvent[];
  verification: {
    replay: "not_started" | "running" | "passed" | "failed" | "unavailable";
    probes: { passed: number; total: number };
    soak: { sessions: number; target: number };
    fixVersion?: string;
  };
  arsImpactNow: number;
  arsImpactOnResolve: number;
  ifUnresolved: string;
  versions: string[];
}

// ─── Activity ────────────────────────────────────────────────────────────────
//
// Generic ledger of everything an agent DID (side-effecting actions) or SAID outward (statements),
// with who it affected, its impact, and whether it was within the agent's authority. Packs attach
// typed detail (e.g. a "commitment" pack classifies statements as promises); the core doesn't.

export type ActivityKind = "action" | "statement";
export type ActivityStatus = "completed" | "pending" | "failed" | "blocked";
export type AuthorityVerdict = "within" | "outside" | "unknown";

export interface AffectedParty {
  kind: "end_user" | "internal_user" | "customer" | "system";
  id: string | null;
  label: string;
}

export interface ActivityRecord {
  id: string;
  ts: ISODate;
  agentId: string;
  agentName: string;
  traceId: string;
  spanId?: string;
  kind: ActivityKind;
  actionClass: string;
  verb: ActionVerb;
  object: ActionObject;
  /** One-line description, e.g. "Deleted 4,212 rows from analytics.events". */
  summary: string;
  /** Quote of the outward statement (kind = statement), privacy-mode dependent. */
  quote?: string;
  affected: AffectedParty | null;
  impact: Impact | null;
  reversible: boolean | null;
  authority: AuthorityVerdict;
  authorityRule?: string;
  /** Statement backed by a tool result / document in the same trace (null = not applicable). */
  grounded: boolean | null;
  groundingSource?: string;
  status: ActivityStatus;
  /** Induced by manipulation from the affected party (excluded from that party's claims). */
  provoked: boolean;
  /** Platform-reported control outcome the action executed (or was blocked) under. */
  controlOutcome?: ControlOutcome;
  deadline?: ISODate;
  /** Pack-specific typing, e.g. { pack: "commitments", type: "refund_promise", label: "Refund promise" }. */
  detail?: { pack: PackId; type: string; label: string };
}

export interface ActivityStats {
  total7d: number;
  outOfPolicy7d: number;
  failed7d: number;
  ungroundedStatements7d: number;
  /** Out-of-policy impact by kind/unit. */
  outOfPolicyImpact7d: Impact[];
  byClass: Array<{ actionClass: string; count: number; outOfPolicy: number }>;
  daily: Array<{ date: ISODate; within: number; outside: number; failed: number }>;
}

// ─── Policies, scenarios, claims ─────────────────────────────────────────────
//
// Loss types: FIRST-PARTY = the customer's own loss (data loss, downtime, cost overrun, remediation,
// honoring an agent's mistake); THIRD-PARTY = loss suffered by someone else the agent acted for or
// talked to (end users, partners). Every scenario declares which it covers; internal agents (coding,
// ops, data) typically have first-party scenarios only.

export const LOSS_TYPES = ["first_party", "third_party"] as const;
export type LossType = (typeof LOSS_TYPES)[number];
//
// The customer (account holder) holds many policies; each policy covers a chosen set of the
// customer's agents and scenarios. End users of the customer are beneficiaries.

export type PolicyStatus = "quote" | "pending_bind" | "active" | "expired" | "cancelled";

export interface CoveragePolicy {
  id: string;
  name: string;
  carrier: string;
  program: string;
  policyNumber: string;
  status: PolicyStatus;
  agentIds: string[];
  periodStart: ISODate;
  periodEnd: ISODate;
  aggregateLimit: Money;
  used: Money;
  deductible: Money;
  perClaimLimit: Money;
  premium: { annual: Money; monthly: Money };
  mode: "graduated" | "strict";
  projectedLimit: Money;
  projectedAt: ISODate;
  limitHistory: Array<{ date: ISODate; limit: number; endorsementId?: string }>;
  stage: "risk_platform" | "telemetry_partner" | "mga";
  scenarioCount: number;
  openClaims: number;
}

export interface Scenario {
  id: string;
  policyId: string;
  policyName: string;
  key: string;
  agentId: string;
  agentName: string;
  actionClass: string;
  envelope: string;
  lossTypes: LossType[];
  /** Impact kind the scenario's exposure is measured in (losses are settled in money). */
  impactKind: ImpactKind;
  state: CoverageState;
  subLimit: Money;
  used: Money;
  perClaimLimit: Money;
  since: ISODate;
  endorsementId: string;
  issueId?: string;
  graceStartedAt?: ISODate;
  graceDeadline?: ISODate;
  graduatedStage?: { coverage: number; coinsurance: number };
  /** Excluded by an underwriter (held until lifted). */
  manualExclusion?: { reason: string; by: string; at: ISODate };
}

export type EndorsementType =
  | "policy_bound"
  | "scenario_added"
  | "scenario_excluded"
  | "scenario_reinstated"
  | "limit_increase"
  | "grace_started"
  | "mitigated"
  | "graduated_step"
  | "re_rating";

export interface Endorsement {
  id: string;
  seq: number;
  type: EndorsementType;
  title: string;
  description: string;
  effectiveAt: ISODate;
  hash: string;
  prevHash: string;
  policyId: string;
  agentId?: string;
  issueId?: string;
}

/** An insurable scenario offered for an agent, used by the quote builder. */
export interface ScenarioCatalogItem {
  key: string;
  agentId: string;
  agentName: string;
  label: string;
  actionClass: string;
  envelope: string;
  lossTypes: LossType[];
  impactKind: ImpactKind;
  /** Pack that supplied this scenario template (null = derived from the core action ontology). */
  pack: PackId | null;
  /** Current state of the scenario on the agent (from its issues and telemetry). */
  state: CoverageState;
  eligible: boolean;
  ineligibleReason?: string;
  /** Annual premium per $1,000 of sub-limit at the agent's current tier. */
  ratePer1k: number;
  suggestedSubLimit: Money;
  /** Policy currently covering this scenario, if any (one active policy per agent-scenario). */
  coveredByPolicyId: string | null;
}

export interface QuoteRequest {
  name: string;
  agentIds: string[];
  scenarios: Array<{ agentId: string; key: string; subLimit: number }>;
  aggregateLimit: number;
  deductible: number;
  perClaimLimit: number;
  mode: "graduated" | "strict";
  termMonths: 12;
}

export interface Quote {
  id: string;
  request: QuoteRequest;
  carrier: string;
  premium: { annual: Money; monthly: Money };
  /** Multiplicative factors shown to the customer to explain the price. */
  factors: Array<{ label: string; value: number; detail: string }>;
  lines: Array<{ agentId: string; key: string; label: string; subLimit: Money; premium: Money; tier: Tier }>;
  warnings: string[];
  expiresAt: ISODate;
  bindable: boolean;
}

/** Public "Insured with traces.insure" profile for an agent platform (no login). */
export interface PlatformProfile {
  platform: { name: string; slug: string; website: string };
  since: ISODate;
  insuredCustomers: number;
  agents: number;
  insuredAgents: number;
  totalInsuredLimit: Money;
  avgArs: number;
  tierDistribution: Array<{ tier: Tier; agents: number }>;
  telemetryCompleteness: number;
  /** Share of control-expected actions that an executing platform reported a control outcome for. */
  controlAdoption: number;
  medianHoursToMitigate: number;
  verifiedFixes90d: number;
  openCriticalIssues: number;
  claimsPaid12m: number;
  topScenarios: Array<{ label: string; policies: number }>;
  monthly: Array<{ month: string; avgArs: number; insuredAgents: number }>;
}

export type ClaimStatus =
  | "prompted"
  | "filed"
  | "evidence_ready"
  | "under_review"
  | "approved"
  | "paid"
  | "denied";

/**
 * Why a claim was denied. The decision note explains the specifics in writing, as claims
 * regulations require for every denial.
 */
export const CLAIM_DENIAL_REASONS = [
  "not_covered",
  "excluded",
  "outside_period",
  "below_deductible",
  "limits_exhausted",
  "provoked",
  "insufficient_evidence",
  "late_notice",
  "duplicate",
  "misrepresentation",
] as const;
export type ClaimDenialReason = (typeof CLAIM_DENIAL_REASONS)[number];

export interface ClaimEvidencePack {
  claimId: string;
  manifestKey: string;
  /** sha256 of the canonical manifest, as stored on the claim and appended to the evidence chain. */
  manifestHash: string;
  manifest: { parts: Array<{ name: string; key: string; sha256: string; bytes: number }>; [k: string]: unknown };
  parts: Record<string, unknown>;
}

export interface ClaimEvent {
  from: ClaimStatus | null;
  to: ClaimStatus;
  /** user email, `carrier:<id>` or `system:…` */
  actor: string;
  note: string;
  at: ISODate;
}

export interface Claim {
  id: string;
  policyId: string;
  policyName: string;
  claimant: string;
  /** end_user: via the customer's public protection page (third-party only); customer: the policyholder. */
  filedBy: "end_user" | "customer" | "auto_prompt";
  lossType: LossType;
  scenarioKey: string;
  agentName: string;
  incidentAt: ISODate;
  createdAt: ISODate;
  loss: Money;
  payout: Money | null;
  status: ClaimStatus;
  provoked: boolean;
  summary: string;
  evidencePackId: string;
  fastTrack: boolean;
  checks: Array<{ label: string; ok: boolean; detail: string }>;
  /** What the policy terms allow (deductible, limits, co-insurance), computed at notice. */
  payable: Money | null;
  /**
   * Who decides: `program` claims are decided in the dashboard by the program's claims handlers
   * (role underwriter); `carrier` claims by the carrier, whose decision arrives by status polling.
   */
  adjudication: "program" | "carrier";
  decision: { outcome: "approved" | "denied"; reason: ClaimDenialReason | null; note: string | null; by: string; at: ISODate } | null;
  payment: { reference: string; at: ISODate } | null;
  /** Status history, oldest first. Only on a single claim (GET /claims/:id). */
  timeline?: ClaimEvent[];
}

// ─── Evidence ────────────────────────────────────────────────────────────────

export interface EvidenceRecord {
  seq: number;
  kind:
    | "control_outcome"
    | "issue_transition"
    | "endorsement"
    | "score_snapshot"
    | "policy_version"
    | "claim"
    | "trace_bundle";
  refId: string;
  summary: string;
  hash: string;
  prevHash: string;
  at: ISODate;
  agentId?: string;
}

export interface EvidenceAnchor {
  date: ISODate;
  merkleRoot: string;
  records: number;
  r2Key: string;
  externalReceipt: string;
  verified: boolean;
}

// ─── Overview & org ──────────────────────────────────────────────────────────

export interface AttentionItem {
  id: string;
  kind:
    | "grace_final"
    | "verification_failed"
    | "telemetry_gap"
    | "material_change"
    | "profile_review"
    | "claim_prompted";
  title: string;
  detail: string;
  severity: Severity;
  at: ISODate;
  href: string;
}

export interface Overview {
  org: { id: string; name: string; plan: string };
  ars: number;
  arsDelta30d: number;
  tier: Tier;
  arsTrend: number[];
  aggregateLimit: Money;
  projectedLimit: Money;
  openIssues: Record<Severity, number>;
  nearestDeadline: { issueId: string; title: string; deadline: ISODate; graceStartedAt: ISODate } | null;
  telemetryCompleteness: number;
  /** Share of control-expected actions that an executing platform reported a control outcome for. */
  controlAdoption: number;
  activePolicies: number;
  annualPremium: Money;
  /** Actions and statements outside the agents' authority in the last 7 days. */
  outOfPolicy7d: { count: number; impact: Impact[] };
  attention: AttentionItem[];
  limitHistory: CoveragePolicy["limitHistory"];
  sessionsDaily: Array<{ date: ISODate; sessions: number; flagged: number }>;
}

export interface Org {
  id: string;
  name: string;
  slug: string;
  plan: string;
  projects: Array<{ id: string; name: string }>;
  user: {
    name: string;
    email: string;
    role: "owner" | "admin" | "engineer" | "product" | "viewer" | "underwriter";
    /** Default home view; users can switch. */
    home: "product" | "engineering" | "risk";
  };
}

// ─── Underwriter ─────────────────────────────────────────────────────────────

export const UNDERWRITER_OVERRIDE_KINDS = ["grace_extension", "manual_exclusion", "lift_exclusion", "re_rating"] as const;
export type UnderwriterOverrideKind = (typeof UNDERWRITER_OVERRIDE_KINDS)[number];

/** An underwriter's override, as recorded and shown to the policyholder. */
export interface UnderwriterOverride {
  id: string;
  kind: UnderwriterOverrideKind;
  agentId: string;
  agentName: string;
  /** The issue (grace extension) or scenario (exclusion) it applies to. */
  targetId: string | null;
  detail: string;
  reason: string;
  actor: string;
  at: ISODate;
}

export interface PortfolioAgent {
  orgName: string;
  platform: string | null;
  agentName: string;
  archetype: Archetype;
  tier: Tier;
  ars: number;
  exposure30d: Money;
  limit: Money;
  openCritical: number;
  completeness: number;
  autonomy: AutonomyLevel;
}

export interface AccumulationNode {
  dependency: string;
  kind: "model" | "mcp_server" | "api" | "framework" | "platform";
  insuredAgents: number;
  limitExposed: Money;
  share: number;
}

export interface Portfolio {
  insuredAgents: number;
  totalLimit: Money;
  avgArs: number;
  lossRatio: number | null;
  arsHistogram: Array<{ bucket: string; count: number }>;
  agents: PortfolioAgent[];
  accumulation: AccumulationNode[];
  tierLossFrequency: Array<{ tier: Tier; incidentsPer1k: number }>;
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: Array<"ingest" | "read" | "admin">;
  /** Set on keys issued to an agent platform: spans sent with it count as reported by that platform. */
  platformId?: string;
  createdAt: ISODate;
  lastUsedAt: ISODate | null;
}

export interface Integration {
  id: string;
  name: string;
  category: "ingest" | "framework" | "payments" | "alerts" | "mcp";
  status: "connected" | "available" | "error";
  detail: string;
}

export interface Settings {
  privacyMode: PrivacyMode;
  /** Semantic enrichment (analytics model) settings; content analysis needs privacy mode full or redacted. */
  enrichment: EnrichmentSettings;
  dataRegion: "us" | "eu";
  retentionDays: number;
  apiKeys: ApiKey[];
  integrations: Integration[];
  members: Array<{ name: string; email: string; role: Org["user"]["role"] }>;
  notifications: Array<{ event: string; channels: Array<"email" | "slack" | "webhook"> }>;
  ingestEndpoint: string;
}

// ─── Public protection page (for the customer's end users) ──────────────────

/** Only third-party scenarios appear; the page exists only while the customer holds third-party cover. */
export interface ProtectionView {
  org: { name: string; slug: string; logoText: string; domain: string };
  carriers: string[];
  policies: Array<{ id: string; name: string; policyNumber: string; validUntil: ISODate }>;
  scenarios: Array<{ key: string; label: string; state: CoverageState; perClaimLimit: Money; policyId: string }>;
  assistants: Array<{ name: string; platform: string | null }>;
  claimsPaid12m: number;
}

// ─── Generic list envelope ───────────────────────────────────────────────────

export interface Page<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
}
